// CANONICAL COPY (GH #7115). src/utils/planGateMarker.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

/**
 * src/utils/planGateMarker.js
 *
 * The one place the client reads what a state-read response WITHHELD.
 *
 * PLAN-B-001's read-side filter (azure-functions/shared/state/planStateFilter.js)
 * empties a gated module's state keys before the response leaves the Function
 * App. Emptying is invisible to whoever reads the key back: a withheld
 * collection and a collection the tenant genuinely does not have are the same
 * bytes. The compliance engine has no plan awareness and no second source of
 * tenant state, so several of its evaluators read an emptied population as
 * "nothing to check (N/A)" and returned pass:true — one vacuous pass carrying a
 * whole strategy:'any' obligation. #6627 is that failure live in production
 * through the already-shipped `assets` gate.
 *
 * So the filter states what it did, in the same response:
 *
 *   state.__planGated = { withheldKeys: { <stateKey>: <moduleKey>, ... } }
 *
 * Read it ONLY through this module. Two rules the helpers below encode:
 *
 *   1. **Absence means "nothing is known", never "nothing was withheld."** A
 *      state built in a test, restored from a cache, or served by a Function
 *      App that predates the marker carries none, and must evaluate exactly as
 *      it does today. This mechanism can only ever ADD not-entitled verdicts.
 *   2. **The marker is not tenant data.** It never enters the blob:
 *      usePersistence.js strips it from the payload and state-write deletes it
 *      from anything that arrives carrying it anyway.
 */

const PLAN_GATE_MARKER_KEY = '__planGated';

/**
 * The `{ stateKey: moduleKey }` map the server reported, or an empty object.
 *
 * @param {object|null|undefined} state
 * @returns {Record<string,string>}
 */
function planWithheldKeyMap(state) {
  const marker = state && state[PLAN_GATE_MARKER_KEY];
  const withheld = marker && marker.withheldKeys;
  return withheld && typeof withheld === 'object' ? withheld : {};
}

/**
 * Of `stateKeys`, the ones this response withheld — each with the module whose
 * plan gate withheld it. Empty array when nothing was withheld or the response
 * carried no marker at all.
 *
 * @param {object|null|undefined} state
 * @param {string[]} stateKeys
 * @returns {Array<{ key: string, module: string }>}
 */
function planWithheldAmong(state, stateKeys) {
  const withheld = planWithheldKeyMap(state);
  const out = [];
  for (const key of stateKeys || []) {
    const moduleKey = withheld[key];
    if (moduleKey) out.push({ key, module: moduleKey });
  }
  return out;
}

/** The distinct module keys behind a planWithheldAmong() result, in order. */
function withheldModuleNames(withheld) {
  const seen = [];
  for (const w of withheld || []) if (!seen.includes(w.module)) seen.push(w.module);
  return seen;
}

/**
 * Tenant-facing label for a module key. The engine's not-entitled summaries are
 * read by the tenant on the obligation screen, so they must name the module the
 * way the product does, never the internal gate key.
 */
const MODULE_LABELS = {
  assets: 'Assets',
  bia: 'Business impact analysis',
  employees: 'Employees',
  findings: 'Findings',
  incidents: 'Incidents',
  integrations: 'Integrations',
  'management-review': 'Management review',
  privacy: 'Privacy',
  threats: 'Threat intelligence',
  vendors: 'Vendors',
  vulnerabilities: 'Vulnerabilities',
  'email-breach-monitoring': 'Email breach monitoring',
};

/** Human label for one module key — falls back to the key itself. */
function moduleLabel(moduleKey) {
  return MODULE_LABELS[moduleKey] || moduleKey;
}

/** "Assets" / "Assets and Employees" / "Assets, Employees and Vendors". */
function moduleLabelList(moduleKeys) {
  const labels = (moduleKeys || []).map(moduleLabel);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

module.exports = {
  PLAN_GATE_MARKER_KEY,
  planWithheldKeyMap,
  planWithheldAmong,
  withheldModuleNames,
  moduleLabel,
  moduleLabelList,
};
